import { Fragment } from "react";

import Link from "@whoppah/next/link";
import { useRouter } from "next/router";

import { IconNavigationChevronRight } from "@/assets/icons/figma/IconNavigationChevronRight";
import { JsonLd } from "@/components/common/JsonLd";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { toAbsoluteUrl, toHref } from "@/features/search/utils/url.utils";

const ITEM_CLASS = "whitespace-nowrap font-body text-sm";

/**
 * Breadcrumb trail for the current result set.
 *
 * Rendered exactly as the API returns it — the trail already starts at Home and ends at the current
 * page, and its titles are localized server-side, so there is nothing to prepend or translate here.
 */
export const SearchBreadcrumbs = () => {
  const { query } = useSearchContext();
  const { locale, defaultLocale } = useRouter();

  /* Crumbs without a title cannot be rendered or described; drop them rather than show a gap. */
  const crumbs = (query.data?.meta.breadcrumbs ?? []).filter(
    (crumb): crumb is { title: string; url: string | null } =>
      Boolean(crumb.title)
  );

  /* A lone "Home" is not a trail. */
  if (crumbs.length < 2) {
    return null;
  }

  return (
    <>
      <nav aria-label="Breadcrumb" className="no-scrollbar overflow-x-auto">
        <ol className="flex items-center space-x-1 text-grey-500">
          {crumbs.map((crumb, index) => {
            const isCurrent = index === crumbs.length - 1;

            return (
              <Fragment key={`${crumb.url ?? "crumb"}-${index}`}>
                {index > 0 && (
                  <li aria-hidden className="flex shrink-0 items-center">
                    <IconNavigationChevronRight className="h-4 w-4" />
                  </li>
                )}
                <li className="flex items-center">
                  {/* The last crumb is the page you are on — describe it, don't link to it. */}
                  {isCurrent || !crumb.url ? (
                    <span aria-current="page" className={ITEM_CLASS}>
                      {crumb.title}
                    </span>
                  ) : (
                    <Link
                      href={toHref(crumb.url)}
                      prefetch={false}
                      className={`${ITEM_CLASS} hover:underline`}
                    >
                      {crumb.title}
                    </Link>
                  )}
                </li>
              </Fragment>
            );
          })}
        </ol>
      </nav>
      <JsonLd
        item={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.title,
            item: toAbsoluteUrl(crumb.url, locale, defaultLocale),
          })),
        }}
      />
    </>
  );
};
